<template>
  <form class="flex flex-col w-full h-full items-stretch gap-y-8" @submit.prevent="applyFilter">
    <section v-for="[name, group] in groups" :key="name" class="flex flex-col gap-y-5">
      <div class="flex justify-between items-center">
        <span class="text-body-regular-bold">
          {{ capitalize(name) }}
        </span>
        <button
          v-if="name === groups[0][0]"
          type="button"
          class="text-body-regular text-info-blue normal-case underline"
          @click.prevent="reset"
        >
          {{ $t("reset") }}
        </button>
      </div>
      <template v-for="(filter, i) of group" :key="i">
        <component
          :is="Filters[filter.type]"
          v-model="internalValue"
          :filter-key="filter.key"
          :options="filter.options"
          class="!w-full"
          :class="filter.class"
        />
      </template>
    </section>
    <section class="action-buttons">
      <custom-button type="submit" variant="success" class="flex-1 whitespace-nowrap">
        {{ $t("toolbar.filter.okButtonText") }}
      </custom-button>
      <custom-button variant="outlined" class="bg-white flex-1" @click.prevent="cancel">
        {{ $t("cancel") }}
      </custom-button>
    </section>
  </form>
</template>

<script lang="ts" setup>
import {
  ref, watch, capitalize, computed,
} from 'vue';
import { useVModel } from '@vueuse/core';
import Filters, { type FilterType } from '@components/utils/filter/filterTypes';
import CustomButton from '@components/utils/CustomButton.vue';
import { useI18n } from 'vue-i18n';
import { useToolbar } from '../toolbar/injects';
import { groupBy } from '@/utils/ds';
import { useNotify } from '../notifications';

export interface Filter {
  key?: string;
  type: FilterType;
  group: string;
  class?: string;
  options?: unknown;
  page?: number;
}

export interface Props {
  modelValue?: Record<string, unknown>;
  filters: Filter[];
}

const internalValue = ref<NonNullable<Props['modelValue']>>({});
const toolbar = useToolbar();

const emit = defineEmits(['update:modelValue', 'cancel', 'update:page']);
const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}),
  filters: () => [],
});

const value = useVModel(props, 'modelValue', emit, { passive: true });

const notify = useNotify();
const { t } = useI18n();

watch(
  value,
  (newValue) => {
    internalValue.value = {
      ...newValue,
    };
  },
  { immediate: true, deep: true },
);

const groups = computed(() => {
  const map = groupBy(props.filters, (v) => v.group);
  return Array.from(map.entries());
});

const applyFilter = () => {
  emit('update:page', 1);
  value.value = internalValue.value;
  toolbar?.close();
  notify?.({
    variant: 'info',
    title: t('toolbar.filter.notifyFilter'),
    duration: 3000,
  });
};
const reset = () => {
  internalValue.value = {};
};
const cancel = () => {
  internalValue.value = value.value;
  toolbar?.close();
};
</script>

<style lang="scss" scoped>
.action-buttons {
  @apply bg-white;
  @apply sticky;
  @apply left-0;
  @apply border-t border-t-element-neutral;
  @apply flex gap-x-2.5 mt-auto;

  bottom: calc(var(--toolbar-padding-y) * -1);
  padding: var(--toolbar-padding-y) var(--toolbar-padding-x);
  margin-left: calc(var(--toolbar-padding-x) * -1);
  margin-right: calc(var(--toolbar-padding-x) * -1);
  margin-bottom: calc(var(--toolbar-padding-y) * -1);
}
</style>
